import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

export type UserStatus = 'pending' | 'approved' | 'rejected';

export interface User {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  isAdmin?: boolean;
  status?: UserStatus;
  // The hero's "how do you know Chris?" note, if they've submitted one.
  note?: string;
}

export interface Session {
  user: User;
  expires: string;
}

interface AuthContextType {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  signIn: () => void;
  signOut: () => void;
  // Re-reads /auth/session — used after a hero submits their note so the
  // pending screen reflects the new state without a full page reload.
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  const setUnauthenticated = useCallback(() => {
    setSession(null);
    setStatus('unauthenticated');
  }, []);

  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch('/auth/session', {
        credentials: 'include',
      });

      if (response.ok) {
        const sessionData = await response.json();
        if (sessionData?.user) {
          setSession(sessionData);
          setStatus('authenticated');
        } else {
          setUnauthenticated();
        }
      } else {
        setUnauthenticated();
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      setUnauthenticated();
    }
  }, [setUnauthenticated]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const signIn = () => {
    window.location.href = '/auth/signin';
  };

  const signOut = async () => {
    try {
      await fetch('/auth/signout', {
        method: 'POST',
        credentials: 'include',
      });
      setSession(null);
      setStatus('unauthenticated');
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ session, status, signIn, signOut, refresh: fetchSession }}>
      {children}
    </AuthContext.Provider>
  );
}
